import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { TrendingUp, Award, Clock, IndianRupee, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const ConsultantProductivity = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/consultants/productivity?days=${days}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('arthik_token')}` }
    }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [days]);

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#94A3B8' }}><Loader2 size={20} className="animate-spin" /> Loading productivity…</div>;
  if (!data || !data.report?.length) return <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94A3B8', fontSize: '0.9rem' }} data-testid="no-productivity-data">No consultants yet — add some in the form above to see productivity here.</div>;

  const { report, totals } = data;
  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
  const performers = [...report].sort((a, b) => b.closed_in_window - a.closed_in_window);

  return (
    <div data-testid="consultant-productivity" style={{ marginBottom: '1.5rem' }}>
      <div className="calculator-section" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrendingUp size={18} style={{ color: '#10B981' }} />
            <h3 style={{ fontSize: '1.05rem', color: '#0F172A', margin: 0, fontWeight: 700 }}>Consultant Productivity</h3>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`productivity-window-${d}`}
                style={{
                  padding: '0.35rem 0.8rem',
                  fontSize: '0.8rem',
                  background: days === d ? '#10B981' : 'white',
                  color: days === d ? 'white' : '#475569',
                  border: '1px solid ' + (days === d ? '#10B981' : '#E2E8F0'),
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Last {d}d
              </button>
            ))}
          </div>
        </div>

        {/* Totals row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
          <TotalCard icon={Award} label="Cases closed" value={totals.total_closed_in_window} hint={`last ${totals.window_days} days`} color="#10B981" />
          <TotalCard icon={IndianRupee} label="Revenue" value={inr(totals.total_revenue_in_window)} hint={`last ${totals.window_days} days`} color="#FF6B35" />
          <TotalCard icon={Clock} label="Open cases" value={totals.total_open_cases} hint="awaiting filing" color="#F59E0B" />
          <TotalCard icon={TrendingUp} label="Consultants" value={totals.consultants} hint="active" color="#3B82F6" />
        </div>

        {/* Per-consultant table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
            <thead>
              <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                <th style={cellHead}>Consultant</th>
                <th style={{ ...cellHead, textAlign: 'center' }}>Open</th>
                <th style={{ ...cellHead, textAlign: 'center' }}>Closed ({days}d)</th>
                <th style={{ ...cellHead, textAlign: 'center' }}>This week</th>
                <th style={{ ...cellHead, textAlign: 'center' }}>Avg turnaround</th>
                <th style={{ ...cellHead, textAlign: 'right' }}>Revenue ({days}d)</th>
                <th style={cellHead}>Trend (4w)</th>
              </tr>
            </thead>
            <tbody>
              {performers.map((r, idx) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #E2E8F0' }} data-testid={`productivity-row-${r.id}`}>
                  <td style={cellBody}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {idx === 0 && r.closed_in_window > 0 && <span title="Top performer" style={{ fontSize: 14 }}>🏆</span>}
                      <div>
                        <div style={{ fontWeight: 600, color: '#0F172A' }}>{r.full_name}</div>
                        <div style={{ fontSize: '0.7rem', color: '#94A3B8' }}>{r.email}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ ...cellBody, textAlign: 'center', fontWeight: 700, color: r.open_cases > 5 ? '#EF4444' : '#0F172A' }}>{r.open_cases}</td>
                  <td style={{ ...cellBody, textAlign: 'center', fontWeight: 700, color: '#10B981' }}>{r.closed_in_window}</td>
                  <td style={{ ...cellBody, textAlign: 'center', color: '#475569' }}>{r.closed_this_week}</td>
                  <td style={{ ...cellBody, textAlign: 'center', color: '#475569' }}>{r.avg_turnaround_days > 0 ? `${r.avg_turnaround_days} days` : '—'}</td>
                  <td style={{ ...cellBody, textAlign: 'right', fontWeight: 700, color: '#FF6B35' }}>{inr(r.revenue_in_window)}</td>
                  <td style={{ ...cellBody, padding: '0.3rem 0.6rem' }}>
                    <div style={{ width: 120, height: 36 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={r.weekly_closed_last_4w} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
                          <Bar dataKey="closed" fill="#10B981" radius={[2, 2, 0, 0]} />
                          <Tooltip wrapperStyle={{ fontSize: '0.75rem' }} formatter={(v) => `${v} closed`} labelFormatter={(l) => `Week of ${l}`} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stacked bar: closed per week summary */}
      <div className="calculator-section" style={{ padding: '1.25rem' }}>
        <h4 style={{ fontSize: '0.95rem', color: '#0F172A', margin: '0 0 0.75rem', fontWeight: 700 }}>Cases closed per week — last 4 weeks</h4>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={aggregateWeekly(report)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="week_start" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="closed" fill="#10B981" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const aggregateWeekly = (report) => {
  const buckets = {};
  report.forEach((r) => {
    (r.weekly_closed_last_4w || []).forEach((w) => {
      buckets[w.week_start] = (buckets[w.week_start] || 0) + w.closed;
    });
  });
  return Object.keys(buckets).sort().map((k) => ({ week_start: k.slice(5), closed: buckets[k] }));
};

const TotalCard = ({ icon: Icon, label, value, hint, color }) => (
  <div style={{ background: 'white', border: '1px solid #E2E8F0', borderLeft: `4px solid ${color}`, borderRadius: 10, padding: '0.85rem 1rem' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
      <Icon size={14} style={{ color }} />
      <span style={{ fontSize: '0.75rem', color: '#64748B', fontWeight: 600 }}>{label}</span>
    </div>
    <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0F172A' }}>{value}</div>
    {hint && <div style={{ fontSize: '0.7rem', color: '#94A3B8', marginTop: 2 }}>{hint}</div>}
  </div>
);

const cellHead = { padding: '0.6rem 0.8rem', fontSize: '0.75rem', color: '#64748B', textAlign: 'left', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' };
const cellBody = { padding: '0.7rem 0.8rem', fontSize: '0.875rem', color: '#0F172A' };

export default ConsultantProductivity;
